import React, { useEffect, useMemo, useState } from 'react';
import { DillButton, DillModal } from '../../../general/Dill';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import {
  blockedTradeReferenceEmailMessage,
  CreditApplication,
  CreditApplicationFieldsValues,
  findBlockedTradeReferenceEmailDomain,
  getBlockedTradeReferenceEmailDomains,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { updateSupplierCreditApplication } from '../../../../redux/services/creditApplicationsService';
import { openDillToast } from '../../../../utils/helpers';
import AppDatePicker from './AppDatePicker';
import { UploadFileInputs } from './UploadFileInput';
import StateDropDown from './StateDropDown';

const CreditAppAddTradeReferenceModal = ({
  open,
  handleClose,
  creditApplication,
  tradeRefFields,
}: {
  open: boolean;
  handleClose: () => void;
  creditApplication: CreditApplication | null;
  tradeRefFields: CreditApplicationFieldsValues[];
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ id: string; error: string }[]>([]);
  const [tradeRefFieldValues, setTradeRefFieldValues] = useState<CreditApplicationFieldsValues[]>(
    []
  );

  const validateFields = () => {
    const errorsField: { id: string; error: string }[] = [];

    const personalEmail = creditApplication?.fieldValues
      .find((f) => f.id === 'email-personal')
      ?.value?.toString()
      .trim()
      .toLowerCase();

    // Existing trade reference emails already saved on the application
    const existingTradeEmails = (creditApplication?.fieldValues ?? [])
      .filter((f) => f.id.includes('trade-reference-company-email') && f.value)
      .map((f) => f.value.toString().trim().toLowerCase());

    // New trade reference email fields being added
    const newEmailFields = tradeRefFieldValues.filter(
      (f) => f.id.includes('trade-reference-company-email') && f.value
    );

    const seenNewEmails: string[] = [];
    const blockedTradeRefDomains = getBlockedTradeReferenceEmailDomains(currentSupplier, [
      creditApplication?.senderEmail,
      personalEmail,
    ]);

    tradeRefFieldValues.forEach((field) => {
      // Required check
      if (field.isRequired && (field.value === '' || field.value == null)) {
        errorsField.push({
          id: field.id,
          error: `${field.name} is required`,
        });
        return;
      }

      // Email validation (only if value exists)
      if (
        field.isFieldEmail &&
        field.value &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.toString())
      ) {
        errorsField.push({
          id: field.id,
          error: 'Invalid email address',
        });
        return;
      }

      // Trade reference email-specific checks
      if (field.id.includes('trade-reference-company-email') && field.value) {
        const emailVal = field.value.toString().trim().toLowerCase();

        if (findBlockedTradeReferenceEmailDomain(emailVal, blockedTradeRefDomains)) {
          errorsField.push({
            id: field.id,
            error: blockedTradeReferenceEmailMessage(currentSupplier?.name),
          });
          return;
        }

        if (personalEmail && emailVal === personalEmail) {
          errorsField.push({
            id: field.id,
            error: 'Trade reference email cannot be the same as your personal email.',
          });
          return;
        }

        if (existingTradeEmails.includes(emailVal) || seenNewEmails.includes(emailVal)) {
          errorsField.push({
            id: field.id,
            error:
              'Duplicate trade reference email. Each trade reference must have a unique email.',
          });
        } else {
          seenNewEmails.push(emailVal);
        }
      }
    });

    setErrors(errorsField);
    return errorsField.length === 0;
  };

  const getFieldError = (fieldId: string) => errors.find((e) => e.id === fieldId)?.error;

  const fieldsGroup = useMemo(() => {
    const grouped = tradeRefFields.reduce((acc, item) => {
      if (!acc[item.index]) acc[item.index] = [];
      acc[item.index].push(item);
      return acc;
    }, {} as Record<number, typeof tradeRefFields>);

    // The new group goes after the highest position already saved for this group — not after the
    // clicked group — since the preview can hide saved groups, and reusing a taken position (or the
    // group-0 id/uniqueId) collides with an existing reference: responses, email changes and status
    // updates then land on the wrong one.
    const sourceField = tradeRefFields[0];
    const nextGroupedPosition =
      Math.max(
        -1,
        ...(creditApplication?.fieldValues ?? [])
          .filter(
            (f) =>
              f.stepNumber === sourceField?.stepNumber &&
              f.groupStepPosition === sourceField?.groupStepPosition &&
              f.groupName === sourceField?.groupName
          )
          .map((f) => f.groupedPosition ?? 0),
        ...tradeRefFields.map((f) => f.groupedPosition ?? 0)
      ) + 1;

    const groupedArray = Object.values(grouped).map((group) => {
      const columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS' =
        group.length === 1 ? 'ONE_COLUMN' : group.length === 2 ? 'TWO_COLUMNS' : 'THREE_COLUMNS';

      return group.map((field) => {
        const { referenceHistoryDate, status, ...rest } = field;

        // Same scheme the application form uses for cloned groups: base id + `-group-N`, and a
        // uniqueId of `${stepName} ${groupName} ${groupStepPosition} ${columnType} ${position} ${id}`.
        const id = `${field.id.replace(/-group-\d+$/, '')}-group-${nextGroupedPosition}`;
        const uniqueId =
          `${field.stepName} ${field.groupName} ${field.groupStepPosition} ${field.groupColumnType} ${nextGroupedPosition} ${id}`
            .toLowerCase()
            .replace(/\s+/g, '-');

        return {
          ...rest,
          id,
          uniqueId,
          columnType,
          groupedPosition: nextGroupedPosition,
        };
      });
    });

    return groupedArray;
  }, [tradeRefFields, creditApplication?.fieldValues]);

  useEffect(() => {
    const flattened = fieldsGroup
      .flat()
      .map((f) => ({ ...f, value: f.fromField === 'DATE' || f.fromField === 'FILE' ? null : '' }));
    setTradeRefFieldValues(flattened);
  }, [fieldsGroup, open]);

  const handleCloseModal = () => {
    setErrors([]);
    setTradeRefFieldValues([]);
    setLoading(false);
    handleClose();
  };

  const handleSubmit = async () => {
    if (creditApplication) {
      if (!validateFields()) return;
      const fieldValues = creditApplication.fieldValues;
      const updatedFieldValue = [...fieldValues, ...tradeRefFieldValues];

      setLoading(true);
      const results = await dispatch(
        updateSupplierCreditApplication({
          supplierId: currentSupplier?.id || '',
          creditApplicationId: creditApplication.id,
          fieldValues: updatedFieldValue,
          savingType: 'overwritefield',
        })
      );
      if (results.type.includes('fulfilled')) {
        openDillToast({
          type: 'SUCCESS',
          message: 'Trade Reference added successfully.',
        });
        setLoading(false);
        handleCloseModal();
      } else {
        openDillToast({
          type: 'ERROR',
          message: 'Failed to add tradde reference.',
        });
        setLoading(false);
      }
    }
  };

  const updateDynamicFieldValue = (id: string, value: any) => {
    setTradeRefFieldValues((prev) =>
      prev.map((field) => (field.id === id ? { ...field, value } : field))
    );
    setErrors((prev) => prev.filter((e) => e.id !== id));
  };

  const getCurrentFields = (
    groupedField: CreditApplicationFieldsValues[],
    columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS'
  ): CreditApplicationFieldsValues[] => {
    const columnCount = columnType === 'ONE_COLUMN' ? 1 : columnType === 'TWO_COLUMNS' ? 2 : 3;

    if (groupedField.length >= columnCount) {
      return groupedField;
    }
    return [...groupedField];
  };

  const renderSignatureFields = (fields: CreditApplicationFieldsValues[]) => {
    return fields.map((field, idx) => {
      const currentValue = tradeRefFieldValues.find((f) => f.id === field.id)?.value;
      const errorMessage = getFieldError(field.id);
      let fieldComponent = null;

      switch (field.fromField) {
        case 'TEXT':
        case 'NUMBER':
        case 'CURRENCY':
        case 'TEXT_AREA':
          fieldComponent = (
            <AppInputField
              label={field.name}
              inputType={
                field.fromField === 'TEXT_AREA'
                  ? 'textarea'
                  : field.fromField === 'NUMBER' || field.fromField === 'CURRENCY'
                  ? 'number'
                  : 'text'
              }
              value={currentValue || ''}
              isRequired={field.isRequired}
              onTextChange={(val) => {
                updateDynamicFieldValue(field.id, val);
              }}
              onBlur={() => {
                if (field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') {
                  updateDynamicFieldValue(field.id, currentValue.trim());
                }
              }}
              errorText={errorMessage}
              dataPrivate={field?.isHashed ? true : false}
            />
          );
          break;

        case 'DATE':
          fieldComponent = (
            <AppDatePicker
              label={field.name}
              selectedDate={currentValue || null}
              isRequired={field.isRequired}
              onDateSelected={(date) => {
                updateDynamicFieldValue(field.id, date);
              }}
              errorText={errorMessage}
            />
          );
          break;

        case 'FILE':
          fieldComponent = (
            <div className="my-1 flex items-center w-full">
              <UploadFileInputs
                isRequired={field.isRequired}
                label={field.name}
                onSelectFile={(file) => {
                  updateDynamicFieldValue(field.id, file.file);
                }}
                errorText={errorMessage}
                selectedFile={currentValue || null}
              />
            </div>
          );
          break;

        case 'STATE_DROPDOWN':
          fieldComponent = (
            <StateDropDown
              isRequired={field.isRequired}
              label={field.name}
              value={currentValue || ''}
              errorText={errorMessage}
              onTextChange={(text) => {
                updateDynamicFieldValue(field.id, text);
              }}
            />
          );
          break;

        default:
          fieldComponent = (
            <div className="text-sm text-gray-400">{`${field.fromField} to be added`}</div>
          );
          break;
      }

      return (
        <div
          key={idx}
          className={`
                  flex-shrink-0
                  flex-grow
                  basis-[280px]
                  sm:basis-[45%]
                  lg:basis-[30%]
                  max-w-full
                `}>
          <div className="px-1 py-2 w-full">{fieldComponent}</div>
        </div>
      );
    });
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Add Trade Reference"
      maxWidth="620px"
      footer={
        <div className="flex justify-end w-full gap-3">
          <DillButton label="Cancel" variant="secondary" onClick={handleCloseModal} />
          <DillButton
            label="Submit"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={!creditApplication || loading}
          />
        </div>
      }>
      <div className="w-full">
        {fieldsGroup.map((group, index) => (
          <div key={`signature_preview_group_${index}`} className="w-full flex flex-wrap">
            {renderSignatureFields(getCurrentFields(group, group?.[0]?.columnType || 'ONE_COLUMN'))}
          </div>
        ))}
      </div>
    </DillModal>
  );
};

export default CreditAppAddTradeReferenceModal;
